import { Fragment, forwardRef, type ComponentType } from "react";
import { cn } from "@/lib/utils";

export interface SourceIconChannel {
  key: string;
  Icon: ComponentType<{ className?: string }>;
  /** Tailwind text-color class, e.g. "text-blue-600". */
  colorClass: string;
}

interface SourceIconClusterProps {
  channels: SourceIconChannel[];
  /** Hover color class applied to the whole button (e.g. "hover:bg-blue-50"). */
  hoverClass?: string;
  /** Optional count badge rendered top-right (e.g. total linked items). */
  badge?: number;
  badgeColorClass?: string;
  ariaLabel: string;
  testId?: string;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  /**
   * Visual size. `"sm"` (default) matches the in-table PreOrderHeader pill
   * row (h-7 / 14px icons / 18px slots). `"md"` is the focus-view variant —
   * h-9, larger icons, wider slots — so the source affordance sits at parity
   * with the other top-bar buttons (Confirm / Ref / Delete @ h-8 + label).
   */
  size?: "sm" | "md";
}

/**
 * Square (h-7 w-7) icon button rendering 1, 2, or 3 source-channel icons
 * side-by-side, each in its own slot. Used by the pre-order header to
 * indicate cross-channel linkage at a glance (e.g. email + whatsapp).
 *
 * - 1 channel: single centered icon (legacy behavior).
 * - 2 channels: vertical split with a 1px divider.
 * - 3 channels: three slots side-by-side with two dividers; icons shrink.
 */
export const SourceIconCluster = forwardRef<
  HTMLButtonElement,
  SourceIconClusterProps
>(function SourceIconCluster(
  {
    channels,
    hoverClass,
    badge,
    badgeColorClass = "bg-blue-600",
    ariaLabel,
    testId,
    disabled,
    onClick,
    size = "sm",
  },
  ref,
) {
  const count = channels.length;
  // Each slot scales with size — sm keeps the table-pill geometry, md bumps
  // height + icon + slot widths so the focus view's actions bar reads as a
  // peer of the other top-bar buttons.
  const heightClass = size === "md" ? "h-9" : "h-7";
  const widthSm =
    count <= 1 ? "w-7" : count === 2 ? "w-[3.25rem]" : "w-[4.5rem]";
  const widthMd = count <= 1 ? "w-9" : count === 2 ? "w-[4.5rem]" : "w-[6rem]";
  const widthClass = size === "md" ? widthMd : widthSm;
  const iconSize = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";

  return (
    <button
      ref={ref}
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        "relative flex-shrink-0 rounded transition-colors",
        heightClass,
        widthClass,
        hoverClass,
        disabled && "opacity-50 cursor-not-allowed",
      )}
    >
      {/* Inner wrapper clips the channel slots to the rounded border so the
          badge can sit outside the button on the top-right corner without
          being trimmed by overflow-hidden. */}
      <span
        className={cn(
          "absolute inset-0 flex items-stretch overflow-hidden rounded border border-gray-200",
          count <= 1 && "justify-center items-center",
        )}
      >
        {channels.map((ch, i) => (
          <Fragment key={ch.key}>
            {i > 0 && <span className="w-px bg-gray-200" aria-hidden="true" />}
            <span
              className={cn(
                "flex-1 flex items-center justify-center",
                ch.colorClass,
              )}
            >
              <ch.Icon className={iconSize} />
            </span>
          </Fragment>
        ))}
      </span>
      {badge != null && badge > 1 && (
        <span
          data-testid="preorder-source-count-badge"
          className={cn(
            "absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full text-white text-[9px] font-semibold leading-[14px] text-center shadow-sm",
            badgeColorClass,
          )}
        >
          {badge}
        </span>
      )}
    </button>
  );
});
